/**
 * The invite a child was in the middle of accepting, kept across the sign-up.
 *
 * The token otherwise lives in the URL and in Auth0's per-tab state, and both
 * are gone the moment the child comes back to the app any other way: a second
 * tab, the link in Auth0's own email, signing out of a parent's session first.
 * They then look like a new member of no gym and are walked through joining
 * one. localStorage survives all of those, so the app can pick the invite up
 * again.
 *
 * Read once and forgotten, so a link opened on a shared device cannot keep
 * sending somebody else back to an invite that is not theirs.
 */
const KEY = 'sc:child-invite'
// The server's own lifetime for an invite. Anything older is dead there too.
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000

export function rememberChildInvite(token: string): void {
  try {
    localStorage.setItem(KEY, JSON.stringify({ token, at: Date.now() }))
  } catch {
    // Storage unavailable: the URL and appState still carry it.
  }
}

export function forgetChildInvite(): void {
  try {
    localStorage.removeItem(KEY)
  } catch {
    // Nothing stored, nothing to forget.
  }
}

/** The remembered token, if there is a fresh one. Forgets it either way. */
export function takeChildInvite(): string | null {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return null
    localStorage.removeItem(KEY)
    const { token, at } = JSON.parse(raw) as { token?: unknown; at?: unknown }
    if (typeof token !== 'string' || !token || typeof at !== 'number') return null
    return Date.now() - at < MAX_AGE_MS ? token : null
  } catch {
    return null
  }
}
